import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { HashRouter } from 'react-router-dom';
import Rotas from '../routes/rotas';
import PoliticaPrivacidade from '../pages/politicaPrivacidade/politicaPrivacidade';
import Navbar from './navbar';
import ThemePicker from './themePicker';
import FormActionEnhancer from './formActionEnhancer';
import TributosApp from '../tributos/TributosApp';
import { applyTheme, getTheme } from '../config/themes';
import axiosAdmin from '../services/axiosAdmin';
import * as actions from '../store/modules/auth/actions';

export default function Body() {
  const isPrivacyPage = window.location.pathname === '/politica-de-privacidade-elooficina'
    || window.location.hash === '#/politica-de-privacidade-elooficina';
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isRefreshingAfterLogin = Boolean(sessionStorage.getItem('eloadmin:refresh-after-login'));
  const showAuthenticatedLayout = isLoggedIn && !isRefreshingAfterLogin;
  const user = useSelector((state) => state.auth.user) || {};
  const corTema = useSelector((state) => state.auth.corTema);
  const posicaoBarra = useSelector((state) => state.auth.posicaoBarra);
  const dispatch = useDispatch();
  const [activeTheme, setActiveTheme] = useState(corTema || 'emerald');
  const [navigationPosition, setNavigationPosition] = useState(posicaoBarra || 'top');
  const theme = getTheme(activeTheme);
  const menuBackground = { background: `linear-gradient(110deg, ${theme.dark}, ${theme.menu})` };

  useEffect(() => {
    const operatorTheme = applyTheme(corTema || 'emerald');
    setActiveTheme(operatorTheme);
  }, [corTema]);

  useEffect(() => {
    const selectedPosition = ['side', 'side-collapsed'].includes(posicaoBarra) ? posicaoBarra : 'top';
    setNavigationPosition(selectedPosition);
    document.documentElement.setAttribute('data-navigation-position', selectedPosition);
  }, [posicaoBarra]);

  const handleNavigationPositionChange = async (position) => {
    const newPosition = ['side', 'side-collapsed'].includes(position) ? position : 'top';
    const previousPosition = navigationPosition;

    try {
      setNavigationPosition(newPosition);
      document.documentElement.setAttribute('data-navigation-position', newPosition);
      if (!user.email) throw new Error('E-mail do usuário não identificado');

      const email = encodeURIComponent(user.email);
      await axiosAdmin.put(`/tema/${email}`, { email: user.email, cor: activeTheme, posicaoBarra: newPosition });
      dispatch(actions.userThemeChange({ posicaoBarra: newPosition }));
      toast.success('Posição do menu atualizada');
    } catch (error) {
      setNavigationPosition(previousPosition);
      document.documentElement.setAttribute('data-navigation-position', previousPosition);
      toast.error('Não foi possível salvar a posição do menu');
    }
  };

  const handleThemeChange = async (themeId) => {
    const newTheme = getTheme(themeId).id;
    const previousTheme = activeTheme;

    try {
      setActiveTheme(newTheme);
      applyTheme(newTheme);
      if (!user.email) throw new Error('E-mail do usuário não identificado');

      const email = encodeURIComponent(user.email);
      await axiosAdmin.put(`/tema/${email}`, { email: user.email, cor: newTheme, posicaoBarra: navigationPosition });
      dispatch(actions.userThemeChange({ corTema: newTheme }));
      toast.success('Cor do tema atualizada');
    } catch (error) {
      setActiveTheme(previousTheme);
      applyTheme(previousTheme);
      toast.error('Não foi possível salvar a cor do tema');
    }
  };

  if (isPrivacyPage) return <PoliticaPrivacidade />;

  // Usuário do tipo TRIBUTOS abre o sistema eloTributos (layout próprio)
  if (showAuthenticatedLayout && user.tipoUsuario === 'TRIBUTOS') {
    return (
      <HashRouter>
        <TributosApp />
      </HashRouter>
    );
  }

  return (
    <HashRouter>
      <div className={showAuthenticatedLayout ? `app-shell ${navigationPosition.startsWith('side') ? 'is-side-navigation' : ''}` : 'auth-shell'}>
        {showAuthenticatedLayout ? (
          <>
            <header className={`app-top-navigation ${navigationPosition.startsWith('side') ? 'is-side-navigation' : ''}`} style={menuBackground}>
              <div className="app-navigation" aria-label="Navegação principal">
                <Navbar menuBackground={menuBackground.background} />
              </div>
              <ThemePicker selectedTheme={activeTheme} onThemeChange={handleThemeChange} navigationPosition={navigationPosition} onNavigationPositionChange={handleNavigationPositionChange} />
              <div className="user-summary"><span className="user-avatar"><i className="pi pi-user" /></span><div><strong>{user.nome || user.email || 'Usuário'}</strong><span>{user.tipoUsuario || 'Acesso autorizado'}</span></div></div>
            </header>
            <div className="app-workspace">
              <FormActionEnhancer />
              <main className="app-content" role="main"><Rotas /></main>
            </div>
          </>
        ) : (
          <main className="auth-content">
            <section className="auth-intro">
              <img src="logo.png" alt="Elo Sistemas" className="auth-logo" />
              <span className="auth-eyebrow">Gestão inteligente</span>
              <h1>Conectando pessoas, soluções e tecnologia.</h1>
              <p>Controle operações, finanças e documentos fiscais em um ambiente único, seguro e eficiente.</p>
              <div className="auth-feature-list">
                <span><i className="pi pi-check-circle" /> Informações centralizadas</span>
                <span><i className="pi pi-check-circle" /> Rotinas mais produtivas</span>
                <span><i className="pi pi-check-circle" /> Acesso protegido</span>
              </div>
            </section>
            <section className="auth-panel">
              <div className="auth-route"><Rotas /></div>
              <span className="auth-version">Elo Sistemas · Versão 2.0.3</span>
            </section>
          </main>
        ) }
      </div>
    </HashRouter>
  );
}
